import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { selectAllQuestionsByFormType } from "../../services/storage/slices";

function useGroupValidation(formRef) {
  const { id: formId, type: formType } = useParams();
  const [groupErrors, setGroupErrors] = useState({});
  const firstErrorRef = useRef(null);

  useEffect(() => {
    setGroupErrors({});
  }, [formId, formType]);

  const questions = useSelector((state) =>
    selectAllQuestionsByFormType(state, { formType, formId }),
    (a, b) => {
      if (a?.length !== b?.length) return false;
      return a.every((q, i) => q?.id === b[i]?.id && q?.groupId === b[i]?.groupId && q?.groupMinimumRequired === b[i]?.groupMinimumRequired);
    }
  );

  // Build group metadata: { groupId: { minimumRequired, questionNames[], questionIds[] } }
  const groups = useMemo(() => {
    const groupMap = {};
    (questions || []).forEach((q) => {
      if (!q?.groupId) return;
      if (!groupMap[q.groupId]) {
        groupMap[q.groupId] = {
          minimumRequired: q.groupMinimumRequired || 1,
          questionNames: [],
          questionIds: [],
        };
      }
      if (q.groupMinimumRequired != null) {
        groupMap[q.groupId].minimumRequired = q.groupMinimumRequired;
      }
      groupMap[q.groupId].questionNames.push(q.name);
      groupMap[q.groupId].questionIds.push(q.id);
    });
    return groupMap;
  }, [questions]);

  const validateGroups = useCallback(() => {
    const form = formRef?.current;
    const errors = {};
    Object.entries(groups).forEach(([groupId, group]) => {
      const yesCount = group.questionNames.filter((name) => {
        if (!form) return false;
        // Radio inputs: check if the "true" option is selected
        const radioInput = form.querySelector(`input[name="${name}"][value="true"]`);
        return radioInput?.checked;
      }).length;
      if (yesCount < group.minimumRequired) {
        errors[groupId] = true;
      }
    });
    setGroupErrors(errors);
    if (Object.keys(errors).length > 0 && firstErrorRef.current) {
      firstErrorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    return Object.keys(errors).length === 0;
  }, [groups, formRef]);

  const clearGroupErrors = useCallback(() => {
    setGroupErrors({});
  }, []);

  return {
    groups,
    groupErrors,
    validateGroups,
    clearGroupErrors,
    firstErrorRef,
  };
}

export default useGroupValidation;
